import { MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { CustomerAddress } from '@nucleus-commerce/api-client/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import {
  useDeleteCustomerAddress,
  useUpdateCustomerAddress,
} from '../hooks/use-customer-address-mutations';
import { useCustomerAddresses } from '../hooks/use-customer-addresses';
import { MAX_ADDRESSES } from '../schemas/customer-rules';

import { CustomerAddressDialog } from './customer-address-dialog';

function addressLines(address: CustomerAddress): string[] {
  const name = [address.firstName, address.lastName].filter(Boolean).join(' ');
  const cityLine = [address.city, address.region, address.postalCode].filter(Boolean).join(', ');
  return [name, address.company, address.line1, address.line2, cityLine, address.countryCode]
    .filter((line): line is string => Boolean(line))
    .map((line) => line.trim());
}

type Editing = { kind: 'new' } | { kind: 'edit'; address: CustomerAddress } | null;

/** The shopper's address book. Staff with `customers.write` keep it for orders taken offline. */
export function CustomerAddressesCard({
  storeId,
  customerId,
}: {
  storeId: string;
  customerId: string;
}) {
  const { t } = useTranslation(['customers', 'common']);
  const { can } = usePermissions();
  const canWrite = can('customers.write');
  const query = useCustomerAddresses(storeId, customerId);
  const addresses = query.data ?? [];
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<CustomerAddress | null>(null);
  const setDefault = useUpdateCustomerAddress(storeId, customerId, {
    successMessage: 'customers:toasts.defaultChanged',
    silentError: false,
  });
  const remove = useDeleteCustomerAddress(storeId, customerId);
  const full = addresses.length >= MAX_ADDRESSES;
  const name = (address: CustomerAddress) => address.label ?? address.line1;

  return (
    <Card className="gap-3" data-tour="customers.detail.addresses">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>{t('customers:addresses.title')}</CardTitle>
        {canWrite && !query.isPending && !query.error ? (
          <Button
            variant="outline"
            size="sm"
            disabled={full}
            onClick={() => setEditing({ kind: 'new' })}
          >
            <Plus aria-hidden />
            {t('customers:addresses.add')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-16" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : addresses.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('customers:addresses.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-4" aria-label={t('customers:addresses.title')}>
            {addresses.map((address) => (
              <li
                key={address.id}
                className="flex items-start justify-between gap-3 text-sm"
                aria-label={name(address)}
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {address.label ? <span className="font-medium">{address.label}</span> : null}
                    {address.isDefaultShipping ? (
                      <Badge variant="outline">{t('customers:addresses.defaultShipping')}</Badge>
                    ) : null}
                    {address.isDefaultBilling ? (
                      <Badge variant="outline">{t('customers:addresses.defaultBilling')}</Badge>
                    ) : null}
                  </div>
                  <address className="text-muted-foreground not-italic">
                    {addressLines(address).map((line, index) => (
                      <span key={index} className="block">
                        {line}
                      </span>
                    ))}
                    {address.phone ? <span className="block">{address.phone}</span> : null}
                  </address>
                </div>
                {canWrite ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('customers:addresses.actionsFor', { name: name(address) })}
                      >
                        <MoreHorizontal aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => setEditing({ kind: 'edit', address })}>
                        {t('common:actions.edit')}
                      </DropdownMenuItem>
                      {address.isDefaultShipping ? null : (
                        <DropdownMenuItem
                          disabled={setDefault.isPending}
                          onSelect={() =>
                            setDefault.mutate({
                              addressId: address.id,
                              body: { isDefaultShipping: true },
                            })
                          }
                        >
                          {t('customers:addresses.makeDefaultShipping')}
                        </DropdownMenuItem>
                      )}
                      {address.isDefaultBilling ? null : (
                        <DropdownMenuItem
                          disabled={setDefault.isPending}
                          onSelect={() =>
                            setDefault.mutate({
                              addressId: address.id,
                              body: { isDefaultBilling: true },
                            })
                          }
                        >
                          {t('customers:addresses.makeDefaultBilling')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(address)}>
                        {t('common:actions.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <p className="text-muted-foreground mt-3 text-xs">
          {canWrite
            ? full
              ? t('customers:addresses.full', { count: MAX_ADDRESSES })
              : t('customers:addresses.hint')
            : t('customers:addresses.readOnlyHint')}
        </p>
      </CardContent>
      {editing ? (
        <CustomerAddressDialog
          storeId={storeId}
          customerId={customerId}
          address={editing.kind === 'edit' ? editing.address : undefined}
          defaultCountry={addresses[0]?.countryCode ?? ''}
          isFirst={addresses.length === 0}
          open
          onOpenChange={(next) => !next && setEditing(null)}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(next) => !next && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('customers:addresses.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting
                ? t('customers:addresses.deleteDescription', { name: name(deleting) })
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleting) remove.mutate(deleting.id);
                setDeleting(null);
              }}
            >
              {t('customers:addresses.deleteSubmit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
